import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CheckCircle2, Loader2, Shield, ShieldCheck, Stethoscope, Video, FileSignature, AlertTriangle } from "lucide-react";
import SiteLayout from "../components/SiteLayout";
import { api } from "../lib/api";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

// -------- Legal copy (kept in-file so the version is auditable via git) --------

const HIPAA_COPY = `Golden Life Standard Health (the "Practice") is committed to protecting the privacy of your health information under the Health Insurance Portability and Accountability Act of 1996 ("HIPAA") and applicable Maryland law. Your protected health information ("PHI") will be used to provide, coordinate, and manage your care; obtain payment for services rendered; and conduct routine healthcare operations such as quality review. Your PHI will not be disclosed to third parties without your written authorization except as required or permitted by law (for example, in response to a court order, to prevent a serious threat to health or safety, or for reporting suspected abuse). You have the right to inspect and copy your record, to request an amendment, to receive an accounting of disclosures, and to receive our full Notice of Privacy Practices upon request. A copy of the full Notice is available at care@goldenlifesh.com.`;

const TELEHEALTH_COPY = `Telehealth services are healthcare services delivered by electronic means, including secure video and audio communication. I understand that (a) my clinician is licensed to practice psychiatry in the State of Maryland and that I must be physically present in Maryland at the time of each session; (b) telehealth carries the same clinical benefits and risks as in-person care, and additional risks specific to technology, including possible interruptions, unauthorized access, and technical failures; (c) my clinician may determine that telehealth is not clinically appropriate for me and refer me to in-person care; (d) in an emergency I will call 911 or the 988 Suicide & Crisis Lifeline rather than rely on telehealth; and (e) sessions will not be recorded without my explicit written consent.`;

const TREATMENT_COPY = `I voluntarily consent to psychiatric evaluation, treatment, and, if clinically indicated, prescription of psychotropic medication by a licensed psychiatric clinician of Golden Life Standard Health. I understand that: (a) results of treatment cannot be guaranteed and that alternative treatments exist; (b) I may withdraw consent at any time; (c) I am responsible for disclosing my full medical history, current medications, and any known allergies; (d) I will not operate a vehicle or heavy machinery under the influence of medication whose sedative effects I have not yet assessed; and (e) I will follow up with my clinician for any adverse effects.`;

const FINANCIAL_COPY = `I understand that visit fees are due at the time of booking (self-pay) or will be billed to my insurance carrier per benefits verification. I acknowledge that co-pays, co-insurance, and any non-covered amounts are my responsibility. Cancellations made less than 24 hours before the scheduled visit, or missed appointments, may be assessed a no-show fee at the discretion of the Practice. Refunds for self-pay visits cancelled at least 24 hours in advance are processed within 5-10 business days.`;

const SECTIONS = [
  { key: "hipaa", title: "1. HIPAA Notice of Privacy Practices", icon: Shield, body: HIPAA_COPY, ack: "I have received and understand the Notice of Privacy Practices." },
  { key: "telehealth", title: "2. Informed Consent to Telehealth", icon: Video, body: TELEHEALTH_COPY, ack: "I consent to receive psychiatric care via telehealth and confirm I will be located in Maryland during sessions." },
  { key: "treatment", title: "3. Consent to Psychiatric Treatment", icon: Stethoscope, body: TREATMENT_COPY, ack: "I consent to psychiatric evaluation, treatment, and prescription of medications where clinically appropriate." },
  { key: "financial", title: "4. Financial Responsibility & Cancellation Policy", icon: FileSignature, body: FINANCIAL_COPY, ack: "I agree to the financial responsibility and 24-hour cancellation policy above." },
];

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
}

export default function Consent() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const appointmentId = useMemo(() => new URLSearchParams(search).get("appointment_id"), [search]);

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ctxError, setCtxError] = useState(null);
  const [acks, setAcks] = useState({ hipaa: false, telehealth: false, treatment: false, financial: false });
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedResult, setSignedResult] = useState(null);

  useEffect(() => {
    if (!appointmentId) {
      setCtxError("Missing appointment reference.");
      setLoading(false);
      return;
    }
    api.get(`/appointments/${appointmentId}/consent-context`)
      .then((r) => { setCtx(r.data); if (r.data.already_signed) setSignedResult({ signed_at: r.data.signed_at, existing: true }); })
      .catch((err) => setCtxError(formatApiError(err.response?.data?.detail) || "Could not load your appointment."))
      .finally(() => setLoading(false));
  }, [appointmentId]);

  const allAcked = acks.hipaa && acks.telehealth && acks.treatment && acks.financial;
  const canSubmit = allAcked && signature.trim().length >= 2 && !submitting;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/consents", {
        appointment_id: appointmentId,
        typed_signature: signature.trim(),
        hipaa_acknowledged: acks.hipaa,
        telehealth_acknowledged: acks.telehealth,
        treatment_acknowledged: acks.treatment,
        financial_acknowledged: acks.financial,
      });
      setSignedResult({ signed_at: data.signed_at, existing: false });
      toast.success("Consent signed and saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <SiteLayout>
      <section className="container-pad max-w-4xl mx-auto py-14">
        <div className="mb-6">
          <span className="pill" data-testid="consent-eyebrow">
            <span className="pill-dot" />
            <span>Required · Legal Consent Form</span>
          </span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold text-ink tracking-tight">
            Patient Consent &amp; Acknowledgement
          </h1>
          <p className="mt-3 text-muted_text leading-relaxed max-w-2xl">
            Please review the sections below carefully. This form is required by law and by our practice policy for every patient before your first telehealth appointment. Your electronic signature has the same legal effect as a written signature.
          </p>
        </div>

        {loading && (
          <div data-testid="consent-loading" className="card-soft p-10 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-sage-700" strokeWidth={1.75} />
            <p className="mt-3 text-sm text-muted_text">Loading your appointment…</p>
          </div>
        )}

        {ctxError && !loading && (
          <div data-testid="consent-error" className="card-soft p-8 text-center border-red-500/20">
            <AlertTriangle className="w-8 h-8 mx-auto text-red-600" strokeWidth={1.75} />
            <p className="mt-3 font-heading text-lg text-ink">We couldn&apos;t find your appointment.</p>
            <p className="mt-2 text-sm text-muted_text">{ctxError}</p>
            <Link to="/contact" className="btn-gold !text-sm mt-6 inline-flex">Book an appointment</Link>
          </div>
        )}

        {ctx && !ctxError && signedResult && (
          <div data-testid="consent-signed-panel" className="card-soft p-8">
            <div className="w-14 h-14 rounded-2xl bg-sage-500/15 text-sage-700 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-ink">Consent recorded.</h2>
            <p className="mt-3 text-muted_text leading-relaxed">
              Thank you{ctx.first_name ? `, ${ctx.first_name}` : ""}. Your signed consent for the{" "}
              <span className="font-medium text-ink">{ctx.service}</span> on{" "}
              <span className="font-medium text-ink">{format(new Date(ctx.date), "EEEE, MMMM d")}</span>{" "}
              at <span className="font-medium text-ink">{formatTime(ctx.time)}</span> was saved on{" "}
              <span data-testid="consent-signed-timestamp" className="font-medium text-ink">
                {format(new Date(signedResult.signed_at), "MMM d, yyyy · h:mm a 'UTC'")}
              </span>.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button data-testid="consent-back-home" onClick={() => navigate("/")} className="btn-gold !text-sm">Back home</button>
              <Link to="/telehealth" data-testid="consent-prepare-visit" className="btn-ghost">How to prepare for my visit</Link>
            </div>
          </div>
        )}

        {ctx && !ctxError && !signedResult && (
          <form onSubmit={submit} data-testid="consent-form" className="space-y-6">
            <div data-testid="consent-appointment-summary" className="card-soft p-6 flex flex-wrap gap-x-8 gap-y-2 text-sm">
              <div><span className="text-muted_text">Patient:</span> <span className="font-medium text-ink">{ctx.first_name}</span></div>
              <div><span className="text-muted_text">Service:</span> <span className="font-medium text-ink">{ctx.service}</span></div>
              <div><span className="text-muted_text">Date:</span> <span className="font-medium text-ink">{format(new Date(ctx.date), "EEE, MMM d yyyy")}</span></div>
              <div><span className="text-muted_text">Time:</span> <span className="font-medium text-ink">{formatTime(ctx.time)} ET</span></div>
              <div className="text-xs text-muted_text w-full mt-1">Document version {ctx.document_version}</div>
            </div>

            {SECTIONS.map((s) => (
              <section key={s.key} data-testid={`consent-section-${s.key}`} className="card-soft p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-sage-500/12 text-sage-700 flex items-center justify-center">
                    <s.icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-ink">{s.title}</h3>
                </div>
                <div className="prose prose-sm max-w-none mt-4 text-muted_text leading-relaxed">
                  <p>{s.body}</p>
                </div>
                <label className="mt-5 flex items-start gap-3 rounded-xl border border-[#E8EBE4] bg-cream/40 p-4 cursor-pointer hover:border-sage-600/30">
                  <input
                    type="checkbox"
                    data-testid={`consent-check-${s.key}`}
                    checked={acks[s.key]}
                    onChange={(e) => setAcks((a) => ({ ...a, [s.key]: e.target.checked }))}
                    className="mt-1 h-5 w-5 accent-sage-700"
                    required
                  />
                  <span className="text-sm text-ink"><span className="font-medium">I acknowledge:</span> {s.ack}</span>
                </label>
              </section>
            ))}

            <section className="card-soft p-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gold-500/15 text-gold-700 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <h3 className="font-heading text-lg font-semibold text-ink">Electronic Signature</h3>
              </div>
              <p className="mt-3 text-sm text-muted_text leading-relaxed">
                By typing your full legal name below you are signing this document electronically. Your signature must exactly match the name on your booking (case does not matter). The date &amp; time of signature is generated by our secure servers and cannot be altered.
              </p>

              <label className="block mt-5 text-xs font-medium tracking-wider uppercase text-muted_text" htmlFor="consent-signature">
                Type your full legal name
              </label>
              <input
                id="consent-signature"
                data-testid="consent-signature-input"
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder={ctx.first_name ? `${ctx.first_name} …` : "Your full legal name"}
                autoComplete="off"
                className="mt-2 w-full rounded-xl border border-[#E8EBE4] bg-white px-4 py-3 font-heading text-2xl italic text-ink tracking-wide focus:outline-none focus:border-sage-600 focus:ring-2 focus:ring-sage-600/20"
                required
              />
              <div className="mt-3 text-xs text-muted_text flex flex-wrap gap-x-6 gap-y-1">
                <span>Signed by: <span className="font-medium text-ink">{signature.trim() || "—"}</span></span>
                <span data-testid="consent-live-timestamp">Date &amp; time (auto): {format(new Date(), "MMM d, yyyy · h:mm a")} local</span>
              </div>
            </section>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-muted_text max-w-md leading-relaxed">
                Your consent is stored securely with the signature, timestamp, and IP address for audit and compliance purposes.
              </p>
              <button
                type="submit"
                data-testid="consent-submit"
                className="btn-gold !rounded-full !text-base !h-12 !px-8 !text-ink !font-semibold disabled:opacity-60"
                disabled={!canSubmit}
              >
                {submitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing…</>) : (<><FileSignature className="w-4 h-4 mr-2" /> Sign &amp; submit consent</>)}
              </button>
            </div>
          </form>
        )}
      </section>
    </SiteLayout>
  );
}
